// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/main/ai/openai-chat-client.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * One OpenAI-compatible chat client for every model the chatbot may use: the
 * AYNI AI gateway (Cloudflare Workers AI) today, a local llama-server, Ollama
 * or vLLM serving a fine-tuned model later. Switching is configuration only
 * (docs/AI_CHATBOT_PLAN.md §3).
 *
 * POST {baseUrl}/chat/completions with stream: true. Text arrives as it is
 * generated; tool calls arrive in fragments keyed by index and are assembled
 * before they are handed over. An endpoint that answers with plain JSON
 * instead of an event stream is read as one message.
 */
import type { AiChatErrorCode } from '../../shared/ai-chat-protocol';
import type { AiToolDefinition } from '../../shared/ai-tools';

export interface ChatToolCall {
  id: string;
  type: 'function';
  function: { name: string; arguments: string };
}

export type ChatMessage =
  | { role: 'system' | 'user'; content: string }
  | { role: 'assistant'; content: string | null; tool_calls?: ChatToolCall[] }
  | { role: 'tool'; content: string; tool_call_id: string };

export interface ChatEndpoint {
  baseUrl: string;
  model: string;
  apiKey: string | null;
}

export type ChatStreamEvent =
  | { type: 'text'; text: string }
  | { type: 'tool_calls'; calls: ChatToolCall[] }
  | { type: 'done'; finishReason: string | null };

/**
 * Why the endpoint's moderation declined a request (the AYNI gateway's 422
 * `content_policy`, docs/AI_CHATBOT_SAFETY.md §5): someone may be in distress,
 * or the question or answer is not one to give.
 */
export type ChatContentPolicy = 'self-harm' | 'declined';

export class ChatEndpointError extends Error {
  constructor(
    public readonly code: AiChatErrorCode,
    message: string,
    public readonly status?: number,
    /** Set when moderation declined the request; the engine then answers kindly instead of failing. */
    public readonly policy?: ChatContentPolicy
  ) {
    super(message);
  }
}

/** The moderation verdict in a 422 body, if that is what it holds. */
function contentPolicyOf(body: string): ChatContentPolicy | null {
  try {
    const error = (JSON.parse(body) as { error?: { type?: unknown; category?: unknown } }).error;
    if (error?.type !== 'content_policy') return null;
    return error.category === 'self_harm' ? 'self-harm' : 'declined';
  } catch {
    return null;
  }
}

export interface StreamChatOptions {
  endpoint: ChatEndpoint;
  messages: ChatMessage[];
  tools?: AiToolDefinition[];
  maxTokens?: number;
  temperature?: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  /** More request headers, e.g. the AYNI gateway's X-Ayni-Moderation for a public surface. */
  headers?: Record<string, string>;
}

/** The AYNI gateway reads a public surface's questions and answers with its guard model (docs/AI_CHATBOT_SAFETY.md §5). */
export const AI_MODERATION_HEADER = 'X-Ayni-Moderation';

/** The chat-completions URL for a base URL, with or without a trailing slash. */
export function chatCompletionsUrl(baseUrl: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/chat/completions`;
}

export function errorForStatus(status: number, body: string): ChatEndpointError {
  const policy = status === 422 ? contentPolicyOf(body) : null;
  if (policy) return new ChatEndpointError('AI_INVALID_REQUEST', 'The request was declined.', status, policy);
  const quota = status === 429 || /quota|neuron|limit exceeded|rate.?limit/i.test(body);
  if (status === 401 || status === 403) {
    return new ChatEndpointError('AI_NOT_CONFIGURED', 'The assistant endpoint refused the key.', status);
  }
  if (quota) {
    return new ChatEndpointError('AI_QUOTA', 'The assistant has used its allowance for now.', status);
  }
  return new ChatEndpointError('AI_UNAVAILABLE', `The assistant endpoint answered ${status}.`, status);
}

interface ToolCallFragment {
  index?: number;
  id?: string;
  function?: { name?: string; arguments?: string };
}

/** Collects tool-call fragments by index into whole calls. */
class ToolCallAssembler {
  private readonly calls = new Map<number, ChatToolCall>();

  add(fragments: ToolCallFragment[]): void {
    fragments.forEach((fragment, position) => {
      const index = typeof fragment.index === 'number' ? fragment.index : position;
      const call = this.calls.get(index) ?? { id: '', type: 'function' as const, function: { name: '', arguments: '' } };
      if (fragment.id) call.id = fragment.id;
      if (fragment.function?.name) call.function.name += fragment.function.name;
      if (fragment.function?.arguments) call.function.arguments += fragment.function.arguments;
      this.calls.set(index, call);
    });
  }

  take(): ChatToolCall[] {
    const calls = [...this.calls.entries()]
      .sort(([a], [b]) => a - b)
      .map(([index, call]) => ({ ...call, id: call.id || `call_${index}` }))
      .filter((call) => call.function.name);
    this.calls.clear();
    return calls;
  }
}

interface ChoiceShape {
  delta?: { content?: string | null; tool_calls?: ToolCallFragment[] };
  message?: { content?: string | null; tool_calls?: ToolCallFragment[] };
  finish_reason?: string | null;
}

export async function* streamChatCompletion(options: StreamChatOptions): AsyncGenerator<ChatStreamEvent> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const headers: Record<string, string> = { ...options.headers, 'Content-Type': 'application/json', Accept: 'text/event-stream' };
  if (options.endpoint.apiKey) headers.Authorization = `Bearer ${options.endpoint.apiKey}`;

  let response: Response;
  try {
    response = await fetchImpl(chatCompletionsUrl(options.endpoint.baseUrl), {
      method: 'POST',
      headers,
      signal: options.signal,
      body: JSON.stringify({
        model: options.endpoint.model,
        messages: options.messages,
        ...(options.tools && options.tools.length ? { tools: options.tools, tool_choice: 'auto' } : {}),
        stream: true,
        // Room for a reasoning model's hidden thinking (Gemma 4 on Workers AI
        // spends ~550 tokens before a short answer); a cut-off thought leaves no answer.
        max_tokens: options.maxTokens ?? 1500,
        temperature: options.temperature ?? 0.3
      })
    });
  } catch (error) {
    if (options.signal?.aborted) throw new ChatEndpointError('AI_CANCELLED', 'The turn was stopped.');
    throw new ChatEndpointError('AI_UNAVAILABLE', `The assistant endpoint could not be reached: ${error instanceof Error ? error.message : String(error)}`);
  }
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw errorForStatus(response.status, body);
  }

  const assembler = new ToolCallAssembler();
  let finishReason: string | null = null;
  const handleChoice = function* (choice: ChoiceShape | undefined): Generator<ChatStreamEvent> {
    if (!choice) return;
    const part = choice.delta ?? choice.message;
    if (part?.content) yield { type: 'text', text: part.content };
    if (part?.tool_calls?.length) assembler.add(part.tool_calls);
    if (choice.finish_reason) finishReason = choice.finish_reason;
  };

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('text/event-stream') || !response.body) {
    const payload = await response.json() as { choices?: ChoiceShape[] };
    yield* handleChoice(payload.choices?.[0]);
  } else {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finished = false;
    try {
      while (!finished) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let newline = buffer.indexOf('\n');
        while (newline >= 0) {
          const line = buffer.slice(0, newline).trim();
          buffer = buffer.slice(newline + 1);
          newline = buffer.indexOf('\n');
          if (!line.startsWith('data:')) continue;
          const data = line.slice(5).trim();
          if (data === '[DONE]') {
            finished = true;
            break;
          }
          let chunk: { choices?: ChoiceShape[]; error?: { message?: string } };
          try {
            chunk = JSON.parse(data) as typeof chunk;
          } catch {
            continue;
          }
          if (chunk.error) throw errorForStatus(500, chunk.error.message ?? '');
          yield* handleChoice(chunk.choices?.[0]);
        }
      }
    } catch (error) {
      if (options.signal?.aborted) throw new ChatEndpointError('AI_CANCELLED', 'The turn was stopped.');
      throw error;
    } finally {
      reader.releaseLock();
    }
  }

  const calls = assembler.take();
  if (calls.length) yield { type: 'tool_calls', calls };
  yield { type: 'done', finishReason };
}
